// src/components/admin/MergeRecordingsModal.jsx
// Multi-select + reorder UI for merging multiple recordings into a single video.
// Default order: ascending by recording_date (then created_at). Admin can reorder
// via up/down arrows before submitting.
//
// On submit:
//   POST /portal/api/course-content/:courseId/recordings/merge
// Backend submits a MediaConvert job; the placeholder row inserted shows up
// with the existing "Processing — ready shortly" badge via VideoPlaylist.
import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiX, FiArrowUp, FiArrowDown, FiCheck, FiAlertCircle, FiLayers,
  FiInfo, FiCalendar,
} from 'react-icons/fi';
import apiService from '../../services/api';
import logger from '../../utils/logger';

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  overflow-y: auto;
`;

const Container = styled(motion.div)`
  background: var(--background-primary);
  border-radius: 14px;
  max-width: 720px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--border-color);
`;

const HeaderIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(184, 149, 106, 0.12);
  color: var(--primary-color);
`;

const Title = styled.h2`
  font-family: 'Inter', sans-serif;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--dashboard-text);
  margin: 0;
  flex: 1;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--text-muted);
  padding: 0.375rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  &:hover { background: var(--background-secondary); }
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
`;

const SectionTitle = styled.h3`
  font-family: 'Inter', sans-serif;
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--text-muted);
  margin: 0 0 0.625rem 0;
`;

const Hint = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.75rem 0.875rem;
  background: rgba(184, 149, 106, 0.08);
  border: 1px solid rgba(184, 149, 106, 0.2);
  border-radius: 8px;
  font-size: 0.8125rem;
  color: var(--text-secondary);
  line-height: 1.4;
  margin-bottom: 1.25rem;

  svg { flex-shrink: 0; margin-top: 1px; color: var(--primary-color); }
`;

const SelectableList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  margin-bottom: 1.5rem;
`;

const SelectableRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 0.875rem;
  background: var(--background-secondary);
  border: 1px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.15s;
  text-transform: none;
  letter-spacing: 0;

  &:has(input:checked) {
    background: rgba(184, 149, 106, 0.08);
    border-color: rgba(184, 149, 106, 0.3);
  }

  input { cursor: pointer; accent-color: var(--primary-color); }

  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RecordingLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  flex: 1;
  min-width: 0;

  .title {
    font-weight: 600;
    color: var(--text-primary);
    font-size: 0.9375rem;
  }
  .meta {
    font-size: 0.75rem;
    color: var(--text-muted);
    display: flex;
    gap: 0.625rem;
    flex-wrap: wrap;
  }
`;

const StatusPill = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0.1875rem 0.5rem;
  border-radius: 999px;
  background: rgba(220, 38, 38, 0.12);
  color: #b91c1c;
`;

const OrderedList = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const OrderedItem = styled.li`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.75rem 0.875rem;
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
`;

const Position = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  border-radius: 7px;
  background: var(--primary-color);
  color: white;
  font-weight: 700;
  font-size: 0.8125rem;
`;

const ItemBody = styled.div`
  flex: 1;
  min-width: 0;
  .title { font-weight: 600; color: var(--text-primary); font-size: 0.9375rem; }
  .meta { font-size: 0.75rem; color: var(--text-muted); margin-top: 0.125rem; }
`;

const ArrowButton = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 7px;
  border: 1px solid var(--border-color);
  background: var(--background-primary);
  color: var(--text-secondary);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background: rgba(184, 149, 106, 0.1);
    color: var(--primary-color);
    border-color: rgba(184, 149, 106, 0.4);
  }
  &:disabled { opacity: 0.35; cursor: not-allowed; }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 120px 1fr;
  gap: 1rem;
  margin-bottom: 1.25rem;

  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`;

const Label = styled.label`
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--text-primary);
`;

const Input = styled.input`
  padding: 0.625rem 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.9375rem;
  background: var(--background-primary);
  color: var(--text-primary);
  &:focus { outline: none; border-color: var(--primary-color); box-shadow: 0 0 0 3px rgba(184, 149, 106, 0.12); }
`;

const Footer = styled.div`
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  background: var(--background-secondary);
`;

const Count = styled.div`
  font-size: 0.8125rem;
  color: var(--text-muted);
  font-weight: 500;
`;

const Buttons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const Btn = styled.button`
  padding: 0.625rem 1.125rem;
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.4375rem;
  border: 1px solid var(--border-color);
  background: var(--background-primary);
  color: var(--text-primary);
  &:hover:not(:disabled) { background: var(--background-secondary); }
  &.primary {
    background: var(--primary-color);
    color: white;
    border-color: var(--primary-color);
    &:hover:not(:disabled) { background: var(--primary-dark, #9a7d57); }
  }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const Alert = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 0.875rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  background: rgba(220, 38, 38, 0.08);
  border: 1px solid rgba(220, 38, 38, 0.25);
  color: #b91c1c;
`;

// ---------- helpers ----------
const sortByDate = (a, b) => {
  const da = a.recording_date || a.created_at || '';
  const db = b.recording_date || b.created_at || '';
  if (da !== db) return da < db ? -1 : 1;
  return (a.created_at || '') < (b.created_at || '') ? -1 : 1;
};

const isReady = (recording) => {
  const status =
    recording?.video_variants?.processing_status ||
    recording?.videoVariants?.processing_status ||
    'ready';
  return status === 'ready';
};

const formatDateShort = (s) => {
  if (!s) return '';
  try {
    const d = new Date(typeof s === 'string' ? s.slice(0, 10) : s);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch (e) { return s; }
};

// ---------- component ----------
function MergeRecordingsModal({ courseId, recordings, onClose, onMergeSubmitted }) {
  const sortedRecordings = useMemo(() => [...(recordings || [])].sort(sortByDate), [recordings]);

  const [selectedIds, setSelectedIds] = useState([]); // ordered: insertion order = selection order
  const [title, setTitle] = useState('');
  const [week, setWeek] = useState('');
  const [recordingDate, setRecordingDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const selectedRecordings = useMemo(() => {
    return selectedIds.map((id) => sortedRecordings.find((r) => r.id === id)).filter(Boolean);
  }, [selectedIds, sortedRecordings]);

  const toggleSelect = (recording) => {
    if (!isReady(recording)) return;
    setSelectedIds((prev) => {
      if (prev.includes(recording.id)) {
        return prev.filter((id) => id !== recording.id);
      }
      // newly selected: append in date order — find correct insert position
      const next = [...prev, recording.id];
      next.sort((a, b) => {
        const ra = sortedRecordings.find((r) => r.id === a);
        const rb = sortedRecordings.find((r) => r.id === b);
        return sortByDate(ra, rb);
      });

      // Default recording date = earliest in selection
      if (!recordingDate) {
        const earliest = sortedRecordings.find((r) => r.id === next[0]);
        if (earliest?.recording_date) {
          setRecordingDate(String(earliest.recording_date).slice(0, 10));
        }
      }
      return next;
    });
  };

  const moveUp = (index) => {
    if (index === 0) return;
    setSelectedIds((prev) => {
      const next = [...prev];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      return next;
    });
  };

  const moveDown = (index) => {
    setSelectedIds((prev) => {
      if (index >= prev.length - 1) return prev;
      const next = [...prev];
      [next[index + 1], next[index]] = [next[index], next[index + 1]];
      return next;
    });
  };

  const canSubmit = selectedIds.length >= 2 && title.trim() && recordingDate;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const payload = {
        recordingIds: selectedIds,
        title: title.trim(),
        recordingDate,
        ...(week ? { week: parseInt(week, 10) } : {}),
      };
      const res = await apiService.mergeCourseRecordings(courseId, payload);
      if (res?.success) {
        onMergeSubmitted?.(res);
        onClose?.();
      } else {
        setError(res?.message || 'Failed to submit merge job');
      }
    } catch (err) {
      logger.error('Merge submission failed', { error: err });
      setError(err?.message || 'Failed to submit merge job');
    } finally {
      setSubmitting(false);
    }
  };

  const readyCount = sortedRecordings.filter(isReady).length;

  return (
    <AnimatePresence>
      <Overlay
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      >
        <Container
          initial={{ y: 24, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 24, opacity: 0 }}
          transition={{ duration: 0.18 }}
        >
          <Header>
            <HeaderIcon><FiLayers size={18} /></HeaderIcon>
            <Title>Merge Recordings</Title>
            <CloseButton onClick={onClose} title="Close" type="button"><FiX size={20} /></CloseButton>
          </Header>

          <Body>
            <Hint>
              <FiInfo size={14} />
              <div>
                Select 2 or more recordings to concatenate into one video. Default order is by
                recording date — use the arrows to reorder. The selected source recordings will
                be <strong>permanently deleted</strong> once the merge completes.
              </div>
            </Hint>

            {error && (
              <Alert>
                <FiAlertCircle size={16} />
                {error}
              </Alert>
            )}

            <SectionTitle>
              Available recordings ({readyCount} ready)
            </SectionTitle>
            <SelectableList>
              {sortedRecordings.length === 0 && (
                <div style={{ padding: '1rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  No recordings to merge.
                </div>
              )}
              {sortedRecordings.map((rec) => {
                const ready = isReady(rec);
                const checked = selectedIds.includes(rec.id);
                return (
                  <SelectableRow key={rec.id} className={!ready ? 'disabled' : ''}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleSelect(rec)}
                      disabled={!ready}
                    />
                    <RecordingLabel>
                      <span className="title">{rec.title}</span>
                      <span className="meta">
                        {rec.recording_date && (
                          <span><FiCalendar size={11} style={{ display: 'inline', verticalAlign: '-2px', marginRight: 2 }} />{formatDateShort(rec.recording_date)}</span>
                        )}
                        {rec.week && <span>Week {rec.week}</span>}
                        {rec.duration && <span>{rec.duration}</span>}
                      </span>
                    </RecordingLabel>
                    {!ready && <StatusPill>Processing</StatusPill>}
                  </SelectableRow>
                );
              })}
            </SelectableList>

            {selectedIds.length > 0 && (
              <>
                <SectionTitle>Merge order ({selectedIds.length} selected)</SectionTitle>
                <OrderedList>
                  {selectedRecordings.map((rec, idx) => (
                    <OrderedItem key={rec.id}>
                      <Position>{idx + 1}</Position>
                      <ItemBody>
                        <div className="title">{rec.title}</div>
                        <div className="meta">{formatDateShort(rec.recording_date)}</div>
                      </ItemBody>
                      <ArrowButton type="button" onClick={() => moveUp(idx)} disabled={idx === 0} title="Move up">
                        <FiArrowUp size={14} />
                      </ArrowButton>
                      <ArrowButton type="button" onClick={() => moveDown(idx)} disabled={idx === selectedRecordings.length - 1} title="Move down">
                        <FiArrowDown size={14} />
                      </ArrowButton>
                    </OrderedItem>
                  ))}
                </OrderedList>

                <div style={{ marginTop: '1.5rem' }}>
                  <SectionTitle>Merged recording details</SectionTitle>
                  <FormGrid>
                    <Field style={{ gridColumn: 'span 3' }}>
                      <Label>Title</Label>
                      <Input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="e.g. Week 2 — Full Session"
                        maxLength={255}
                      />
                    </Field>
                    <Field>
                      <Label>Recording date</Label>
                      <Input
                        type="date"
                        value={recordingDate}
                        onChange={(e) => setRecordingDate(e.target.value)}
                      />
                    </Field>
                    <Field>
                      <Label>Week (optional)</Label>
                      <Input
                        type="number"
                        min="1" max="52"
                        value={week}
                        onChange={(e) => setWeek(e.target.value)}
                        placeholder="—"
                      />
                    </Field>
                  </FormGrid>
                </div>
              </>
            )}
          </Body>

          <Footer>
            <Count>
              {selectedIds.length >= 2
                ? `${selectedIds.length} recordings will be merged`
                : selectedIds.length === 1
                  ? 'Select at least 1 more recording'
                  : 'Select 2 or more recordings'}
            </Count>
            <Buttons>
              <Btn type="button" onClick={onClose} disabled={submitting}>Cancel</Btn>
              <Btn type="button" className="primary" onClick={handleSubmit} disabled={!canSubmit || submitting}>
                <FiCheck size={14} />
                {submitting ? 'Submitting…' : 'Merge recordings'}
              </Btn>
            </Buttons>
          </Footer>
        </Container>
      </Overlay>
    </AnimatePresence>
  );
}

export default MergeRecordingsModal;
